/* eslint-disable no-useless-catch */
import { ENV } from "../utils";

export class Competence {
  baseApi = ENV.BASE_API;

  /**
   * Fetches all competences with optional filters.
   * @param {string} accessToken - JWT token
   * @param {Object} filters - Optional filters { subjectId, gradeId, status }
   * @returns {Promise<Array>} - List of competences
   */
  async getCompetences(accessToken, filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.subjectId) queryParams.append("subjectId", filters.subjectId);
      if (filters.gradeId) queryParams.append("gradeId", filters.gradeId);
      if (filters.status !== undefined)
        queryParams.append("status", filters.status);

      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}?${queryParams.toString()}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches competences by subject and grade.
   * @param {string} accessToken - JWT token
   * @param {number} subjectId - Subject ID
   * @param {number} gradeId - Grade ID
   * @returns {Promise<Array>} - List of competences
   */
  async getCompetencesBySubjectAndGrade(accessToken, subjectId, gradeId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}/subject/${subjectId}/grade/${gradeId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches a competence by ID.
   * @param {string} accessToken - JWT token
   * @param {number} competenceId - Competence ID
   * @returns {Promise<Object>} - Competence object
   */
  async getCompetence(accessToken, competenceId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}/${competenceId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Creates a new competence.
   * @param {string} accessToken - JWT token
   * @param {Object} data - Competence data { code, name, description, subjectId, gradeId, order }
   * @returns {Promise<Object>} - Created competence
   */
  async createCompetence(accessToken, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates an existing competence.
   * @param {string} accessToken - JWT token
   * @param {number} competenceId - Competence ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} - Updated competence
   */
  async updateCompetence(accessToken, competenceId, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}/${competenceId}`;
      const params = {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Deletes a competence (soft delete).
   * @param {string} accessToken - JWT token
   * @param {number} competenceId - Competence ID
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteCompetence(accessToken, competenceId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}/${competenceId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reorders competences.
   * @param {string} accessToken - JWT token
   * @param {Object} data - { subjectId, gradeId, competences: [{ id, order }] }
   * @returns {Promise<Object>} - Result
   */
  async reorderCompetences(accessToken, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}/reorder`;
      const params = {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Toggles competence status (active/inactive).
   * @param {string} accessToken - JWT token
   * @param {number} competenceId - Competence ID
   * @returns {Promise<Object>} - Updated competence
   */
  async toggleCompetenceStatus(accessToken, competenceId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.COMPETENCE}/${competenceId}/toggle-status`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }
}
